'use client';

import { useClientTranslation } from '@lib/i18n/client';
import GetSlash from '@modules/get-slash-internet-card/lib/get-slash';
import { Alert } from '@tuic/web-compositions/core/Alert';
import { Button } from '@tuic/web-compositions/core/Button';
import { useCallback, useEffect, useState } from 'react';

import { getPhaserAttributesFromUrl } from '../utils';

import type { GetSlashCredentials } from '@lib/third-parties/get-slash.types';
import type { TFunction } from '@tuic/i18n';
import type { ButtonProps } from '@tuic/web-compositions/core/Button';
import type { FC, MouseEventHandler } from 'react';

type Props = {
  credentials?: GetSlashCredentials;
};

export const GetSlashDebugButton: FC<Props> = ({ credentials }) => {
  const { t } = useClientTranslation();
  const [pending, setPending] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>();
  const [debugData, setDebugData] = useState<string>();
  const [useDebugEndpoints, setUseDebugEndpoints] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const attrs = getPhaserAttributesFromUrl(window.location.href);
      setUseDebugEndpoints(attrs.mockInternetCard === true);
    }
  }, []);

  const handleGetData: MouseEventHandler = useCallback(
    async ev => {
      ev.preventDefault();

      if (typeof credentials === 'undefined') {
        return;
      }

      setPending(true);

      const getSlash = new GetSlash({
        credentials,
        useDebugEndpoints: useDebugEndpoints,
      });

      const result = await getSlash.getDebugData();
      const jsonData = JSON.stringify(result, null, 2);
      setDebugData(jsonData);

      setPending(false);
    },
    [credentials, useDebugEndpoints],
  );

  const handleCopyData: MouseEventHandler = useCallback(
    async ev => {
      ev.preventDefault();

      if (!debugData) {
        return;
      }

      try {
        await navigator.clipboard.writeText(debugData);
        setCopied(true);
      } catch (e) {
        setCopied(false);
      }

      setDebugData(undefined);
      setTimeout(() => setCopied(undefined), 3000);
    },
    [debugData],
  );

  return (
    <div className="space-y-6">
      {typeof credentials === 'undefined' && (
        <Alert mode="attention" headline={t('Zugangsdaten fehlen')}>
          {t(
            'Debug-Daten sind nur abrufbar, wenn zuvor Ihre persönlichen Zugangsdaten für GetSlash generiert werden konnten.',
          )}
        </Alert>
      )}

      <Button
        onClick={debugData ? handleCopyData : handleGetData}
        variant="secondary"
        indicating={pending}
        disabled={typeof credentials === 'undefined'}
        {...getButtonProps(t, debugData, copied)}
      />
    </div>
  );
};

const getButtonProps = (
  t: TFunction,
  debugData: string | undefined,
  copied: boolean | undefined,
): ButtonProps => {
  if (copied === true) {
    return {
      iconLeft: 'check',
      children: t('Erfolgreich kopiert'),
    };
  }

  if (copied === false) {
    return {
      iconLeft: 'warning-hexagon',
      children: t('Kopieren gescheitert'),
    };
  }

  if (debugData) {
    return {
      iconLeft: 'receipt',
      children: t('Daten in Zwischenablage kopieren'),
    };
  }

  return {
    iconLeft: 'cloud',
    children: t('GetSlash Debug-Daten abrufen'),
  };
};
